/*
 * 输入框旁的设置入口.
 *   - dev/test/staging: 齿轮 → 完整调试 popover(context 用量 + 显示详细工具 toggle)
 *   - prod: 完整调试面板隐藏, 只在原齿轮位置暴露"复制定位"按钮 —— 线上排查
 *     问题需要用户把 team_no / user_id / session_id 复制给我们定位日志.
 *
 * 上层 ChatPanel 透传 sessionId / conversationId / groupId / userId, 让 hook 拉对应 context_usage.
 */
import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Popover, Switch, Tag } from 'antd'
import { isDebugEnvEnabled, getBuildEnv } from './envGuard'
import { useAiClerkSettings } from './useSettings'
import { setShowToolDetails } from '../../../slices/aiClerkSettingsSlice'
import { useSessionContextUsage } from './useSessionContextUsage'
import { ContextUsageIndicator } from './ContextUsageIndicator'
import type { RootState } from '../../../app/store'
import './SettingsPopover.scss'

interface Props {
  sessionId?: string
  conversationId?: string
}

/** 从 redux 拉 groupId / userId / teamNo (= group.invite_id, 跟 api.ts:getTeamNo 同口径) */
function useDebugIdentity(): {
  groupId: string
  userId: string
  teamNo: string
} {
  return useSelector((s: RootState) => {
    try {
      const main: any = (s as any)?.main
      const group: any = (s as any)?.group
      const userId = main?.user?.user_id || ''
      const groupId = group?.groupId || ''
      const teamNo = group?.invite_id || ''
      return { groupId, userId, teamNo }
    } catch {
      return { groupId: '', userId: '', teamNo: '' }
    }
  })
}

/** 兼容 HTTP / 非安全上下文的剪贴板写入 (navigator.clipboard 在 HTTPS / localhost 才可用) */
async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      // 安全上下文限制, 走 fallback
    }
  }
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.cssText = 'position:fixed;opacity:0;left:-9999px'
  document.body.appendChild(textarea)
  textarea.select()
  try {
    document.execCommand('copy')
    return true
  } catch {
    return false
  } finally {
    document.body.removeChild(textarea)
  }
}

/** 复制会话定位三段 (team_no / user_id / session_id) — 对齐 XHeyClaw-Web DebugSessionIdButton.
 *  staging 排查 NAS jsonl 文件路径用: /home/node/.openclaw/users/<user_id>/sessions/<session_id>.jsonl
 */
const DebugCopyIdButton: React.FC<{
  teamNo?: string
  userId?: string
  sessionId: string
}> = ({ teamNo, userId, sessionId }) => {
  const [copied, setCopied] = useState(false)
  const payload = useMemo(() => {
    const lines = [
      teamNo ? `team_no: ${teamNo}` : null,
      userId ? `user_id: ${userId}` : null,
      sessionId ? `session_id: ${sessionId}` : null
    ].filter(Boolean)
    return lines.join('\n')
  }, [teamNo, userId, sessionId])

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      // 在 antd Popover 内部, 不要冒泡触发外层 popover 关闭
      e.stopPropagation()
      if (!payload) return
      const ok = await copyToClipboard(payload)
      if (ok) {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      }
    },
    [payload]
  )

  return (
    <button
      type="button"
      className={`aiClerk-settings__copy-btn${copied ? ' is-copied' : ''}`}
      onClick={handleClick}
      disabled={!payload}
      title={copied ? '已复制' : `复制会话定位三段:\n${payload || '(暂无)'}`}
    >
      <span className="aiClerk-settings__copy-icon" aria-hidden>
        {copied ? '✓' : '⧉'}
      </span>
      <span>{copied ? '已复制' : '复制定位'}</span>
    </button>
  )
}

// 跟 chat 走同口径: URL 派生为空时 (新会话场景, URL 干净) fallback 到
// localStorage 里 chatStream 用的同一个 conversationId (api.ts:CONV_KEY).
// 否则 hook 收到空 id 直接 disable, popover 永远显示 "上下文 --".
const CONV_KEY = 'aiClerk:conversationId'
function readActiveConversationId(): string {
  try {
    return window.localStorage.getItem(CONV_KEY) || ''
  } catch {
    return ''
  }
}

const SettingsContent: React.FC<Props> = (props) => {
  const dispatch = useDispatch()
  const { showToolDetails } = useAiClerkSettings()
  const { groupId, userId, teamNo } = useDebugIdentity()
  const effectiveConvId =
    props.conversationId || props.sessionId || readActiveConversationId()
  const usage = useSessionContextUsage({
    groupId,
    userId,
    sessionId: effectiveConvId,
    conversationId: effectiveConvId,
    enabled: !!groupId
  })

  return (
    <div className="aiClerk-settings">
      <div className="aiClerk-settings__header">
        <span className="aiClerk-settings__header-title">调试设置</span>
        <div className="aiClerk-settings__header-actions">
          <Tag color="orange">{getBuildEnv()}</Tag>
          <DebugCopyIdButton
            teamNo={teamNo}
            userId={userId}
            sessionId={effectiveConvId}
          />
        </div>
      </div>

      <div className="aiClerk-settings__row">
        <span className="aiClerk-settings__label">上下文用量</span>
        <ContextUsageIndicator usage={usage} />
      </div>

      <div className="aiClerk-settings__row">
        <span className="aiClerk-settings__label">显示详细工具</span>
        <Switch
          checked={showToolDetails}
          onChange={(v: boolean) => dispatch(setShowToolDetails(v))}
        />
      </div>

      <div className="aiClerk-settings__hint">
        默认极简模式: 流式中底部&quot;思考中…&quot;会拼最近一个工具的状态文案
        (例如&quot;正在读取文档&quot;). 开启上方 toggle 可看到完整工具列表.
      </div>
    </div>
  )
}

/** prod 环境: 不渲染完整调试 popover, 只在原齿轮位置暴露"复制定位"按钮.
 *  方便线上用户一键把 team_no / user_id / session_id 复制给我们排查日志.
 *  只读 redux 身份 + localStorage conversationId, 不发任何额外请求. */
const ProdCopyLocator: React.FC<Props> = (props) => {
  const { userId, teamNo } = useDebugIdentity()
  const effectiveConvId =
    props.conversationId || props.sessionId || readActiveConversationId()
  return (
    <DebugCopyIdButton
      teamNo={teamNo}
      userId={userId}
      sessionId={effectiveConvId}
    />
  )
}

export const SettingsPopover: React.FC<Props> = (props) => {
  // prod: 完整调试面板隐藏, 但保留"复制定位"按钮(放在原齿轮的位置)
  if (!isDebugEnvEnabled()) return <ProdCopyLocator {...props} />

  return (
    <Popover
      content={<SettingsContent {...props} />}
      trigger="click"
      placement="topRight"
      classNames={{ root: 'aiClerk-settings__popover' }}
    >
      <button
        type="button"
        className="aiClerk-settings__btn"
        title={`调试设置 (${getBuildEnv()} 环境)`}
        aria-label="调试设置"
      >
        ⚙
      </button>
    </Popover>
  )
}

export default SettingsPopover
